import { useEffect, useId, useState, type SyntheticEvent } from 'react';
import {
  describeExportFilter,
  EXPORT_FORMATS,
  EXPORT_OPERATORS,
  exportFormat,
  exportTable,
  fmt,
  groupRows,
  isActiveFilter,
  longDate,
  matchesAll,
  type CsvValue,
  type ExportFilter,
  type ExportFormat,
  type ExportOp,
} from '@/shared/lib';
import { Button } from './Button';
import { Segmented } from './Controls';
import { Modal } from './Dialogs';

export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** The table's name: the dialog's subtitle and the file name. */
  name: string;
  headers: readonly string[];
  /** Every exportable row as cells, in the table's order (a paged table fetches them here). */
  load: () => Promise<readonly (readonly CsvValue[])[]>;
  /** Rows the table's source holds, when more than the loader fetches (`limit`). */
  total?: number | undefined;
  limit?: number | undefined;
}

type Rows =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; cells: readonly (readonly CsvValue[])[] };

const FORMAT_KEY = 'export.format';

function storedFormat(): ExportFormat {
  try {
    const value = localStorage.getItem(FORMAT_KEY);
    return EXPORT_FORMATS.some((f) => f.id === value) ? (value as ExportFormat) : 'csv';
  } catch {
    return 'csv';
  }
}

function storeFormat(format: ExportFormat) {
  try {
    localStorage.setItem(FORMAT_KEY, format);
  } catch {
    // A private window or blocked storage: the choice just is not remembered.
  }
}

const FORMAT_OPTIONS = EXPORT_FORMATS.map((f) => ({ value: f.id, label: f.label }));

/**
 * `dialog.modal.modal--export`: choose the file format (CSV, Excel, PDF), the columns, filters on
 * the exported values and a column to group by, see how many records the file will hold, then
 * save it. The rows load when the dialog opens.
 */
export function ExportDialog({ open, onClose, ...props }: ExportDialogProps) {
  const id = useId();
  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy={`${id}-title`}
      id={`${id}-export`}
      className="modal--export"
    >
      <ExportForm key={String(open)} id={id} onClose={onClose} {...props} />
    </Modal>
  );
}

function ExportForm({
  id,
  onClose,
  name,
  headers,
  load,
  total,
  limit,
}: Omit<ExportDialogProps, 'open'> & { id: string }) {
  const [rows, setRows] = useState<Rows>({ status: 'loading' });
  // The rows load once when the dialog opens (the form remounts per opening); Retry loads again.
  const [loading, setLoading] = useState(() => load());
  const [format, setFormat] = useState<ExportFormat>(storedFormat);
  const [included, setIncluded] = useState<boolean[]>(() => headers.map(() => true));
  const [filters, setFilters] = useState<ExportFilter[]>([]);
  const [groupBy, setGroupBy] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    loading.then(
      (cells) => {
        if (live) setRows({ status: 'ready', cells });
      },
      (error: unknown) => {
        console.error('Export rows could not be loaded', error);
        if (live) setRows({ status: 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [loading]);

  const active = filters.filter(isActiveFilter);
  const loaded = rows.status === 'ready' ? rows.cells : [];
  const matching = active.length > 0 ? loaded.filter((cells) => matchesAll(cells, active)) : loaded;
  const columns = headers
    .map((header, index) => ({ header, index }))
    .filter((c) => included[c.index]);
  const capped = total !== undefined && loaded.length < total;
  const canExport = rows.status === 'ready' && matching.length > 0 && columns.length > 0;

  const countText = () => {
    if (rows.status === 'loading') {
      const expected = total === undefined ? undefined : Math.min(total, limit ?? total);
      return expected === undefined
        ? 'Loading the records…'
        : `Loading ${fmt.count(expected, 'record')}…`;
    }
    if (rows.status === 'failed') return 'The records could not be loaded.';
    const all = fmt.count(loaded.length, 'record');
    const base = active.length > 0 ? `${fmt.num(matching.length)} of ${all} match` : all;
    return capped
      ? `${base} · the first ${fmt.num(loaded.length)} of ${fmt.num(total)} in the table's order`
      : base;
  };

  const choose = (next: ExportFormat) => {
    setFormat(next);
    storeFormat(next);
  };

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!canExport) return;
    const projected = matching.map((cells) => columns.map((c) => cells[c.index]));
    const groupIndex = groupBy === null ? -1 : columns.findIndex((c) => c.index === groupBy);
    const grouped =
      groupIndex >= 0
        ? groupRows(projected, groupIndex, columns[groupIndex]?.header ?? '')
        : { rows: projected, groups: undefined };
    const notes = [
      `Exported ${longDate(new Date().toISOString().slice(0, 10))}`,
      countText(),
      ...active.map((filter) => describeExportFilter(filter, headers)),
      ...(groupIndex >= 0 ? [`Grouped by ${columns[groupIndex]?.header ?? ''}`] : []),
    ];
    exportTable(
      {
        name,
        subtitle: notes.join(' · '),
        headers: columns.map((c) => c.header),
        rows: grouped.rows,
        ...(grouped.groups ? { groups: grouped.groups } : {}),
      },
      format,
    );
    onClose();
  };

  const addFilter = () => {
    setFilters([...filters, { column: columns[0]?.index ?? 0, op: 'contains', value: '' }]);
  };
  const setFilter = (index: number, next: ExportFilter) => {
    setFilters(filters.map((filter, i) => (i === index ? next : filter)));
  };
  const toggleColumn = (index: number) => {
    setIncluded(included.map((on, i) => (i === index ? !on : on)));
  };

  return (
    <form className="modal__form export" onSubmit={submit} noValidate>
      <h2 className="modal__title" id={`${id}-title`}>
        Export
      </h2>
      <p className="export__name">{name}</p>

      <div className="field">
        <span className="field__label" id={`${id}-format`}>
          Format
        </span>
        <div className="export__format">
          <Segmented label="Format" options={FORMAT_OPTIONS} value={format} onChange={choose} />
          <p className="field__hint">{exportFormat(format).hint}</p>
        </div>
      </div>

      <div className="field">
        <span className="field__label">
          <span id={`${id}-columns`}>Columns</span>
          <span className="actions">
            <span>
              {fmt.num(columns.length)} of {fmt.num(headers.length)}
            </span>
            <Button
              variant="link"
              onClick={() => {
                setIncluded(headers.map(() => true));
              }}
            >
              All
            </Button>
            <Button
              variant="link"
              onClick={() => {
                setIncluded(headers.map(() => false));
              }}
            >
              None
            </Button>
          </span>
        </span>
        <div
          className="option-chips export__columns"
          role="group"
          aria-labelledby={`${id}-columns`}
        >
          {headers.map((header, index) => (
            <button
              key={index}
              type="button"
              className="option-chip"
              aria-pressed={included[index] ?? false}
              onClick={() => {
                toggleColumn(index);
              }}
            >
              {header}
            </button>
          ))}
        </div>
        <p className="field__error" role="alert" hidden={columns.length > 0}>
          Select at least one column.
        </p>
      </div>

      <div className="field">
        <span className="field__label">
          <span id={`${id}-filters`}>Filter</span>
          <Button variant="link" onClick={addFilter}>
            Add filter
          </Button>
        </span>
        <ul className="filter-list" aria-labelledby={`${id}-filters`}>
          {filters.map((filter, index) => (
            <FilterRow
              // Rows have no identity beyond their position; editing replaces the row in place.
              key={index}
              filter={filter}
              headers={headers}
              onChange={(next) => {
                setFilter(index, next);
              }}
              onRemove={() => {
                setFilters(filters.filter((_, i) => i !== index));
              }}
            />
          ))}
        </ul>
        <p className="field__hint" hidden={filters.length > 0}>
          No filter: every record of the table is exported. Filters apply to the exported values.
        </p>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={`${id}-group`}>
          Group by
        </label>
        <select
          className="select select--compact"
          id={`${id}-group`}
          value={groupBy === null ? '' : String(groupBy)}
          onChange={(event) => {
            setGroupBy(event.target.value === '' ? null : Number(event.target.value));
          }}
        >
          <option value="">No grouping</option>
          {columns.map((c) => (
            <option key={c.index} value={c.index}>
              {c.header}
            </option>
          ))}
        </select>
        <p className="field__hint">
          Sorts the records by this column; Excel and PDF add a heading per group.
        </p>
      </div>

      <p className="export__count" role="status" aria-live="polite">
        <strong>{countText()}</strong>
        {rows.status === 'failed' && (
          <Button
            variant="link"
            onClick={() => {
              setRows({ status: 'loading' });
              setLoading(load());
            }}
          >
            Retry
          </Button>
        )}
      </p>

      <div className="actions export__actions">
        <Button variant="link" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="small" type="submit" icon="download" disabled={!canExport}>
          Export {exportFormat(format).label}
        </Button>
      </div>
    </form>
  );
}

interface FilterRowProps {
  filter: ExportFilter;
  headers: readonly string[];
  onChange: (filter: ExportFilter) => void;
  onRemove: () => void;
}

function FilterRow({ filter, headers, onChange, onRemove }: FilterRowProps) {
  const needsValue = EXPORT_OPERATORS.find((o) => o.id === filter.op)?.needsValue ?? true;
  const header = headers[filter.column] ?? '';
  return (
    <li className="filter-row">
      <div className="filter-row__line">
        <select
          className="select select--compact"
          aria-label="Column"
          value={filter.column}
          onChange={(event) => {
            onChange({ ...filter, column: Number(event.target.value) });
          }}
        >
          {headers.map((h, index) => (
            <option key={index} value={index}>
              {h}
            </option>
          ))}
        </select>
        <select
          className="select select--compact"
          aria-label="Condition"
          value={filter.op}
          onChange={(event) => {
            onChange({ ...filter, op: event.target.value as ExportOp });
          }}
        >
          {EXPORT_OPERATORS.map((op) => (
            <option key={op.id} value={op.id}>
              {op.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="icon-btn"
          aria-label={`Remove filter ${header}`}
          onClick={onRemove}
        >
          ×
        </button>
      </div>
      {needsValue && (
        <div className="filter-row__value">
          <input
            className="input input--sm"
            type="text"
            aria-label={`Value for ${header}`}
            placeholder="Value"
            value={filter.value}
            onChange={(event) => {
              onChange({ ...filter, value: event.target.value });
            }}
          />
        </div>
      )}
    </li>
  );
}
